import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import AdminPage from '../../components/admin/AdminPage';
import Loader from '../../components/common/Loader';
import NoData from '../../components/common/NoData';
import { getGenericSurveyAnalytics } from '../../services/adminService';

function SurveyAnalytics() {
  const navigate = useNavigate();
  const { id } = useParams();
  const surveyId = Number(id);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('responses');
  const [query, setQuery] = useState('');
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [responses, setResponses] = useState([]);
  const [responsesMeta, setResponsesMeta] = useState({ limit: 50, offset: 0, has_more: false, q: '' });
  const [loadingMore, setLoadingMore] = useState(false);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  };

  const fetchAnalytics = async (opts = {}) => {
    if (!surveyId) {
      setLoading(false);
      return;
    }

    const nextLimit = Math.min(Math.max(Number(opts.limit ?? responsesMeta.limit ?? 50), 1), 200);
    const nextOffset = Math.max(Number(opts.offset ?? 0), 0);
    const nextQ = String(opts.q ?? query ?? '').trim();
    const append = Boolean(opts.append);

    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const resp = await getGenericSurveyAnalytics(surveyId, {
        limit: nextLimit,
        offset: nextOffset,
        q: nextQ,
      });
      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to load analytics');
        setData(null);
        return;
      }

      setData((prev) => {
        if (!prev) return resp.data;
        return {
          ...resp.data,
          survey: resp.data?.survey ?? prev?.survey,
          questions: resp.data?.questions ?? prev?.questions,
          total_responses: resp.data?.total_responses ?? prev?.total_responses,
        };
      });

      const nextRows = Array.isArray(resp.data?.responses) ? resp.data.responses : [];
      setResponses((prev) => {
        if (!append) return nextRows;
        const seen = new Set(prev.map((r) => r?.response_id));
        const merged = [...prev];
        for (const r of nextRows) {
          if (!seen.has(r?.response_id)) merged.push(r);
        }
        return merged;
      });

      setResponsesMeta(resp.data?.responses_meta || { limit: nextLimit, offset: nextOffset, has_more: false, q: nextQ });
    } catch (e) {
      toast.error('Failed to load analytics');
      setData(null);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setResponses([]);
    setResponsesMeta({ limit: 50, offset: 0, has_more: false, q: '' });
    fetchAnalytics({ limit: 50, offset: 0, q: '' });
  }, [surveyId]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSelectedResponse(null);
      fetchAnalytics({ limit: responsesMeta.limit || 50, offset: 0, q: query });
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const totalResponses = data?.total_responses || 0;
  const responseRows = useMemo(() => (Array.isArray(responses) ? responses : []), [responses]);

  const questions = useMemo(() => (Array.isArray(data?.questions) ? data.questions : []), [data?.questions]);

  const optionTextByQuestionAndId = useMemo(() => {
    const m = new Map();
    for (const q of questions) {
      const qm = new Map();
      for (const o of q.options || []) qm.set(Number(o.id), o.option_text);
      m.set(Number(q.question_id), qm);
    }
    return m;
  }, [questions]);

  const filteredResponses = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return responseRows;
    return responseRows.filter((r) => {
      const hay = [
        r?.respondent_name,
        r?.respondent_email,
        r?.user_name,
        r?.user_email,
        r?.source,
        formatDateTime(r?.createdAt),
        r?.response_id,
      ]
        .filter(Boolean)
        .join(' | ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [responseRows, query]);

  const formatResponseDisplayName = (r) => {
    const respondent = [r?.respondent_name, r?.respondent_email].filter(Boolean).join(' • ');
    const internalUser = [r?.user_name, r?.user_email].filter(Boolean).join(' • ');
    if (respondent) return { primary: respondent, secondary: r?.source === 'public' ? 'Public' : 'Internal' };
    if (internalUser) return { primary: internalUser, secondary: r?.user_id ? `User #${r.user_id}` : 'Internal' };
    return { primary: r?.user_id ? `User #${r.user_id}` : 'Anonymous', secondary: r?.source || '-' };
  };

  const normalizeSelectedOptionIds = (raw) => {
    if (raw === null || raw === undefined) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const downloadCsv = () => {
    try {
      const headers = [
        'response_id',
        'submitted_at',
        'source',
        'user_id',
        'user_name',
        'user_email',
        'respondent_name',
        'respondent_email',
        'answers_json',
      ];
      const lines = [headers.join(',')];
      for (const r of responseRows) {
        const row = [
          r?.response_id ?? '',
          r?.createdAt ?? '',
          r?.source ?? '',
          r?.user_id ?? '',
          (r?.user_name ?? '').toString().replaceAll('"', '""'),
          (r?.user_email ?? '').toString().replaceAll('"', '""'),
          (r?.respondent_name ?? '').toString().replaceAll('"', '""'),
          (r?.respondent_email ?? '').toString().replaceAll('"', '""'),
          JSON.stringify(r?.answers || {}).replaceAll('"', '""'),
        ].map((v) => `"${String(v ?? '').replaceAll('\n', ' ').replaceAll('\r', ' ')}"`);
        lines.push(row.join(','));
      }
      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeTitle = String(data?.survey?.title || 'survey').replace(/[^a-z0-9-_]+/gi, '_');
      a.href = url;
      a.download = `${safeTitle}_responses.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export');
    }
  };

  if (loading) {
    return (
      <AdminPage
        back={
          <button
            type="button"
            onClick={() => navigate('/admin/surveys')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back to Surveys
          </button>
        }
        title="Analytics"
      >
        <Loader message="Loading analytics..." />
      </AdminPage>
    );
  }

  if (!data) {
    return (
      <AdminPage
        back={
          <button
            type="button"
            onClick={() => navigate('/admin/surveys')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back to Surveys
          </button>
        }
        title="Analytics"
      >
        <NoData message="No analytics available" />
      </AdminPage>
    );
  }

  return (
    <AdminPage
      back={
        <button
          type="button"
          onClick={() => navigate('/admin/surveys')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
          Back to Surveys
        </button>
      }
      title="Survey Analytics"
      description={data?.survey?.title ? `Results for \"${data.survey.title}\"` : 'Survey results'}
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setSelectedResponse(null);
              setQuery('');
              fetchAnalytics({ limit: responsesMeta.limit || 50, offset: 0, q: '' });
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
          >
            <Icon icon="fluent:arrow-clockwise-24-regular" width="18" height="18" />
            Refresh
          </button>
        </div>
      }
    >
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500">Total responses</div>
            <div className="text-2xl font-semibold text-gray-900">{totalResponses}</div>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Icon icon="fluent:people-24-regular" width="22" height="22" className="text-primary" />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('responses')}
            className={`px-3 py-2 rounded-xl text-sm font-semibold border ${
              activeTab === 'responses' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
            }`}
          >
            Responses
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`px-3 py-2 rounded-xl text-sm font-semibold border ${
              activeTab === 'overview' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
            }`}
          >
            Overview
          </button>
        </div>
      </div>

      {activeTab === 'responses' ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
          <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">Responses</div>
              <div className="text-xs text-gray-500 mt-0.5">Latest {responseRows.length} submissions</div>

              <div className="mt-3">
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Icon icon="fluent:search-24-regular" width="18" height="18" className="text-gray-400" />
                  </div>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name/email/source/date"
                    className="w-full max-w-lg rounded-xl border border-gray-200 bg-white pl-10 pr-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={downloadCsv}
                disabled={responseRows.length === 0}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
              >
                <Icon icon="fluent:arrow-download-24-regular" width="18" height="18" />
                Export CSV
              </button>
            </div>
          </div>

          {filteredResponses.length === 0 ? (
            <div className="p-5">
              <div className="text-sm text-gray-500">No responses found</div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredResponses.map((r) => {
                const d = formatResponseDisplayName(r);
                return (
                  <div key={r.response_id} className="p-4 hover:bg-gray-50/60 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{d.primary}</div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{formatDateTime(r.createdAt)}</span>
                        <span className="text-gray-300">|</span>
                        <span>{r.source || '-'}</span>
                        <span className="text-gray-300">|</span>
                        <span>{d.secondary}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setSelectedResponse(r)}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary text-white px-3 py-2 text-sm font-semibold hover:opacity-95"
                      >
                        <Icon icon="fluent:open-24-regular" width="18" height="18" />
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {responsesMeta?.has_more ? (
            <div className="p-5 border-t border-gray-100 flex items-center justify-center">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => {
                  fetchAnalytics({
                    limit: responsesMeta.limit || 50,
                    offset: (responsesMeta.offset || 0) + (responsesMeta.limit || 50),
                    q: responsesMeta.q || query,
                    append: true,
                  });
                }}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
              >
                {loadingMore ? (
                  <Icon icon="fluent:spinner-ios-20-regular" width="18" height="18" className="animate-spin" />
                ) : (
                  <Icon icon="fluent:add-24-regular" width="18" height="18" />
                )}
                Load more
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {activeTab === 'overview' ? (
        questions.length === 0 ? (
          <NoData message="No questions" />
        ) : (
          <div className="space-y-4">
            {questions.map((q, idx) => (
              <div key={q.question_id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="text-sm font-semibold text-gray-900 break-words">
                  {idx + 1}. {q.question_text}
                </div>
                <div className="text-xs text-gray-500 mt-1">{q.type}</div>

                {q.type === 'text' ? (
                  <div className="mt-4">
                    {(q.text_answers || []).length === 0 ? (
                      <div className="text-sm text-gray-500">No text answers yet</div>
                    ) : (
                      <div className="space-y-2">
                        {q.text_answers.slice(0, 50).map((t, i) => (
                          <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap">
                            {t}
                          </div>
                        ))}
                        {q.text_answers.length > 50 ? (
                          <div className="text-xs text-gray-500">Showing first 50 answers</div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 space-y-2">
                    {(q.options || []).map((o) => {
                      const pct = totalResponses > 0 ? Math.round((Number(o.count || 0) / totalResponses) * 100) : 0;
                      return (
                        <div key={o.id} className="rounded-xl border border-gray-200 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm text-gray-900 min-w-0 truncate">{o.option_text}</div>
                            <div className="text-xs font-semibold text-gray-700">{o.count} ({pct}%)</div>
                          </div>
                          <div className="mt-2 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-2 bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : null}

      {selectedResponse ? (
        <div className="fixed inset-0 z-50">
          <button type="button" onClick={() => setSelectedResponse(null)} className="absolute inset-0 bg-black/30" />
          <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col">
            <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Response details</div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatDateTime(selectedResponse.createdAt)} • {selectedResponse.source || '-'} • #{selectedResponse.response_id}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedResponse(null)}
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 hover:bg-gray-50"
              >
                <Icon icon="fluent:dismiss-24-regular" width="18" height="18" className="text-gray-700" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <div className="text-xs text-gray-500">Respondent</div>
                <div className="text-sm font-semibold text-gray-900 mt-1">
                  {formatResponseDisplayName(selectedResponse).primary}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {[
                    selectedResponse.respondent_name,
                    selectedResponse.respondent_email,
                    selectedResponse.user_name,
                    selectedResponse.user_email,
                  ]
                    .filter(Boolean)
                    .join(' • ') || '-'}
                </div>
              </div>

              <div className="mt-5 space-y-3">
                {questions.map((q, idx) => {
                  const a = selectedResponse?.answers?.[q.question_id];
                  let display = '-';

                  if (!a) {
                    display = '-';
                  } else if (q.type === 'text') {
                    display = a.answer_text ? String(a.answer_text) : '-';
                  } else if (q.type === 'single_choice') {
                    const t = optionTextByQuestionAndId.get(Number(q.question_id))?.get(Number(a.selected_option_id));
                    display = t || (a.selected_option_id ? `Option #${a.selected_option_id}` : '-');
                  } else {
                    const ids = normalizeSelectedOptionIds(a.selected_option_ids);
                    const texts = ids
                      .map((rawId) => {
                        const id = Number(rawId);
                        const t = optionTextByQuestionAndId.get(Number(q.question_id))?.get(id);
                        return t || (Number.isFinite(id) ? `Option #${id}` : null);
                      })
                      .filter(Boolean);
                    display = texts.length > 0 ? texts.join(', ') : '-';
                  }

                  return (
                    <div key={q.question_id} className="rounded-2xl border border-gray-100 p-4">
                      <div className="text-xs text-gray-500">Q{idx + 1}</div>
                      <div className="text-sm font-semibold text-gray-900 mt-1">{q.question_text}</div>
                      <div className="text-sm text-gray-800 mt-2 whitespace-pre-wrap">{display}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPage>
  );
}

export default SurveyAnalytics;
